import {
  Children,
  cloneElement,
  Fragment,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
  useRef,
} from "react";
import { Dialog } from "@base-ui/react/dialog";
import { z } from "zod";
import { authClient } from "./auth-client";
import "./dashboard-components.css";

export function Brand({ inverse = false }: { inverse?: boolean }) {
  return (
    <a className={`brand ${inverse ? "brand--inverse" : ""}`} href="/">
      <span className="brand__mark" aria-hidden="true">
        B
      </span>
      <span>
        The Buying
        <br />
        Lane
      </span>
    </a>
  );
}

export function Button({
  children,
  tone = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: "primary" | "secondary" | "quiet" | "danger";
}) {
  return (
    <button className={`button button--${tone} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function LinkButton({
  children,
  href,
  tone = "primary",
}: {
  children: ReactNode;
  href: string;
  tone?: "primary" | "secondary" | "quiet";
}) {
  return (
    <a className={`button button--${tone}`} href={href}>
      {children}
    </a>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className = "",
  group = false,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
  group?: boolean;
}) {
  const fieldId = useId();
  const describedBy = [
    hint ? `${fieldId}-hint` : null,
    error ? `${fieldId}-error` : null,
  ].filter((id): id is string => Boolean(id));
  const fieldContent = augmentFieldControls(children, describedBy, Boolean(error));
  const required = Children.toArray(children).some(
    (child) =>
      isValidElement<{ required?: boolean }>(child) && child.props.required,
  );
  const content = (
    <>
      {fieldContent}
      {hint ? <span id={describedBy[0]} className="field__hint">{hint}</span> : null}
      {error ? <span id={describedBy.at(-1)} className="field__error">{error}</span> : null}
    </>
  );
  if (group) {
    return (
      <fieldset
        className={`field choice-field ${className}`}
        data-required={required ? "true" : "false"}
      >
        <legend className="field__label">
          {label}
          <span className="field__requirement" aria-hidden="true" />
        </legend>
        {content}
      </fieldset>
    );
  }
  return (
    <label
      className={`field ${className}`}
      data-required={required ? "true" : "false"}
    >
      <span className="field__label">
        {label}
        <span className="field__requirement" aria-hidden="true" />
      </span>
      {content}
    </label>
  );
}

type FieldControlProps = {
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "false" | "true";
};

function isFieldControl(type: ReactElement["type"]): boolean {
  return type === "input" || type === "select" || type === "textarea" || type === Input || type === Select || type === Textarea;
}

function mergeDescribedBy(current: string | undefined, ids: string[]): string | undefined {
  const values = new Set((current ?? "").split(/\s+/).filter(Boolean));
  ids.forEach((id) => values.add(id));
  return values.size ? [...values].join(" ") : undefined;
}

function augmentFieldControls(
  children: ReactNode,
  describedBy: string[],
  invalid: boolean,
): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<FieldControlProps & { children?: ReactNode }>(child)) return child;
    if (isFieldControl(child.type)) {
      return cloneElement(child, {
        "aria-describedby": mergeDescribedBy(child.props["aria-describedby"], describedBy),
        "aria-invalid": child.props["aria-invalid"] ?? (invalid ? true : undefined),
      });
    }
    if (child.type === Fragment) {
      return cloneElement(child, undefined, augmentFieldControls(child.props.children, describedBy, invalid));
    }
    return child;
  });
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input" {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="input textarea" {...props} />;
}

export function Money({
  cents,
  currency = "USD",
  compact = false,
}: {
  cents?: number | null;
  currency?: string;
  compact?: boolean;
}) {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return <span className={compact ? "money money--compact" : "money"}>—</span>;
  }

  return (
    <span className={compact ? "money money--compact" : "money"}>
      {(cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: compact ? 0 : 2,
      })}
    </span>
  );
}

const statusKeys = [
  "intake",
  "requirements_locked",
  "sourcing",
  "quote_ready",
  "approved",
  "dealer_reconfirmed",
  "handed_off",
  "completed",
  "no_qualifying_offer",
  "refund_pending",
  "refunded",
  "cancelled",
  "verified",
  "candidate",
  "connected",
  "completed_call",
] as const;

const statusLabel = {
  intake: "Intake",
  requirements_locked: "Search confirmed",
  sourcing: "Sourcing",
  quote_ready: "Offers ready",
  approved: "Customer approved",
  dealer_reconfirmed: "Dealer reconfirmed",
  handed_off: "Handoff complete",
  completed: "Deal secured",
  no_qualifying_offer: "Search closed",
  refund_pending: "Return processing",
  refunded: "Returned",
  cancelled: "Cancelled",
  verified: "Verified",
  candidate: "Candidate",
  connected: "Connected",
  completed_call: "Completed",
} satisfies Record<(typeof statusKeys)[number], string>;

export function displayStatus(status?: string): string {
  const parsed = z.enum(statusKeys).safeParse(status);
  return parsed.success
    ? statusLabel[parsed.data]
    : status?.replaceAll("_", " ") ?? "Unknown";
}

export function Badge({
  status,
  children,
}: {
  status?: string;
  children?: ReactNode;
}) {
  const tone =
    status === "completed" ||
    status === "verified" ||
    status === "approved_retrieval" ||
    status === "refunded"
      ? "success"
      : status === "cancelled" ||
          status === "rights_hold" ||
          status === "rejected"
        ? "danger"
        : status === "approved" ||
            status === "dealer_reconfirmed" ||
            status === "handed_off" ||
            status === "no_qualifying_offer" ||
            status === "refund_pending"
          ? "signal"
          : "neutral";
  return (
    <span className={`badge badge--${tone}`}>
      <span className="badge__dot" aria-hidden="true" />
      {children ?? displayStatus(status)}
    </span>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  body,
  action,
}: {
  eyebrow?: string;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-title">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
        {body ? <p>{body}</p> : null}
      </div>
      {action ? <div className="section-title__action">{action}</div> : null}
    </div>
  );
}

export function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__line" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="loading__spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Notice({
  tone = "info",
  title,
  children,
}: {
  tone?: "info" | "warning" | "success" | "danger";
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : undefined}>
      <strong>{title}</strong>
      <div>{children}</div>
    </div>
  );
}

export function Modal({
  title,
  eyebrow,
  description,
  children,
  onClose,
  closeDisabled = false,
}: {
  title: string;
  eyebrow?: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog.Root
      open
      disablePointerDismissal={closeDisabled}
      onOpenChange={(nextOpen, details) => {
        if (nextOpen) return;
        if (closeDisabled) {
          if ("event" in details) details.event.preventDefault();
          return;
        }
        onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="modal-backdrop" />
        <Dialog.Viewport className="modal-viewport">
          <Dialog.Popup
            className="modal"
            initialFocus={() =>
              popupRef.current?.querySelector<HTMLElement>(
                "[data-modal-initial-focus], [autofocus]",
              )
            }
            ref={popupRef}
          >
            <header className="modal__header">
              <div>
                {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
                <Dialog.Title>{title}</Dialog.Title>
                {description ? <Dialog.Description>{description}</Dialog.Description> : null}
              </div>
              <Dialog.Close
                className="button button--quiet"
                disabled={closeDisabled}
                aria-label="Close dialog"
              >
                Close
              </Dialog.Close>
            </header>
            {children}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function AppShell({
  children,
  active,
  operator = false,
}: {
  children: ReactNode;
  active?: string;
  operator?: boolean;
}) {
  const links = operator
    ? [
        ["/operator", "Deals"],
        ["/operator/research", "Research library"],
      ]
    : [["/portal", "My deals"]];
  const session = authClient.useSession();
  return (
    <div
      className={`app-shell ${operator ? "app-shell--operator" : "buyer-theme app-shell--buyer"}`}
    >
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="app-header">
        <Brand />
        <nav aria-label="Primary">
          {links.map(([href, label]) => (
            <a
              href={href}
              key={href}
              className={active === href ? "is-active" : undefined}
              aria-current={active === href ? "page" : undefined}
            >
              {label}
            </a>
          ))}
          {session.data ? (
            <button
              className="app-header__sign-out"
              type="button"
              onClick={() =>
                void authClient.signOut().then(() => navigateAfterSignOut())
              }
            >
              Sign out
            </button>
          ) : null}
        </nav>
      </header>
      <main id="main-content" tabIndex={-1}>{children}</main>
      <footer className="app-footer" aria-label="Legal">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
      </footer>
    </div>
  );
}

function navigateAfterSignOut(): void {
  window.location.assign("/");
}

export function DetailList({
  items,
  ...props
}: {
  items: Array<[string, ReactNode]>;
} & HTMLAttributes<HTMLDListElement>) {
  return (
    <dl className="detail-list" {...props}>
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value === null || value === undefined || value === "" ? "—" : value}</dd>
        </div>
      ))}
    </dl>
  );
}
